<script lang="ts" setup>
import { computed } from 'vue';
import ExpandCollapseToggle from '@Components/ExpandCollapseToggle.vue';
import KeyValueDetails, {
    type KeyValueSection,
} from '@Components/KeyValueDetails.vue';
import type { WiringInstructions } from '@Types/WiringInstrucions.interface';
import { formatAddressLines } from '@Use/useAddressFormatter';

const props = withDefaults(defineProps<{
    wiringInstructions: WiringInstructions;
    toggleDataId?: string;
    collapseInitialState?: 'opened' | 'closed';
}>(), {
    collapseInitialState: 'closed',
});

const hasFurtherCreditTo = (wi: WiringInstructions): boolean =>
    !!(wi.furtherCreditTo?.accountName || wi.furtherCreditTo?.accountNumber);

const buildCollectSections = (wi: WiringInstructions): KeyValueSection[] => {
    const sections: KeyValueSection[] = [
        {
            title: 'credit to',
            rows: [
                {
                    type: 'stacked',
                    label: 'Account holder',
                    values: [
                        wi.creditTo?.accountName,
                        ...formatAddressLines(wi.personalAddress),
                    ],
                    valueWeight: 'normal',
                },
                {
                    type: 'labeled',
                    label: 'Account number',
                    value: wi.creditTo?.accountNumber,
                    valueWeight: 'bold',
                    sensitive: true,
                },
                {
                    type: 'labeled',
                    label: 'Account type',
                    value: wi.bankAccountInfo?.accountType,
                    valueWeight: 'bold',
                },
            ],
        },
    ];

    if (hasFurtherCreditTo(wi)) {
        sections.push({
            title: 'Final credit to',
            rows: [
                {
                    type: 'labeled',
                    label: 'Account holder',
                    value: wi.furtherCreditTo.accountName,
                    valueWeight: 'normal',
                },
                {
                    type: 'labeled',
                    label: 'Account number',
                    value: wi.furtherCreditTo.accountNumber,
                    valueWeight: 'bold',
                    sensitive: true,
                },
            ],
        });
    }

    sections.push({
        title: 'Beneficiary bank',
        rows: [
            {
                type: 'plain', value: wi.beneficiaryBank?.name,
                valueWeight: 'normal'
            },
            {
                type: 'labeled',
                label: 'Routing number',
                value: wi.beneficiaryBank?.routingNumber,
                valueWeight: 'bold',
                sensitive: true,
            },
        ],
    });

    return sections;
};

const sections = computed<KeyValueSection[]>(() =>
    buildCollectSections(props.wiringInstructions),
);
</script>

<template>
    <app-collapse
        class="collect-account-info"
        :initial-state="collapseInitialState"
    >
        <template #trigger="slotProps">
            <expand-collapse-toggle
                :is-open="slotProps?.isOpen"
                open-label="Hide account details"
                closed-label="Show account details"
                :data-id="toggleDataId"
            />
        </template>
        <key-value-details :sections="sections" />
    </app-collapse>
</template>

<style lang="scss" scoped>
.collect-account-info {
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden;

    // this has to be locally scoped for now
    :deep(.trigger-indicator) {
        display: none;
    }
}
</style>
